/**
 * Terminal 82 - Price Picker (Simplified)
 * 
 * Clean scrollable list with native scroll behavior
 */

export interface PricePickerOptions {
  currentPrice: number;
  minPercent?: number;
  maxPercent?: number;
  step?: number;
  onSelect?: (price: number, percent: number) => void;
}

export interface PricePickerState {
  selectedPrice: number;
  selectedPercent: number;
  selectedTier: string;
  prices: number[];
  currentIndex: number;
}

function calculateStep(price: number): number {
  if (price < 100) return 1;
  if (price < 500) return 2;
  if (price < 1000) return 5;
  if (price < 5000) return 10;
  return 25;
}

function roundToStep(price: number, step: number): number {
  return Math.round(price / step) * step;
}

function formatPrice(price: number): string {
  return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatPercent(percent: number): string {
  const sign = percent >= 0 ? '+' : '';
  return sign + percent.toFixed(2) + '%';
}

function getTier(percent: number): string {
  const abs = Math.abs(percent);
  if (abs >= 30) return 'mythic';
  if (abs >= 20) return 'legendary';
  if (abs >= 12) return 'epic';
  if (abs >= 7) return 'rare';
  if (abs >= 4) return 'uncommon';
  return 'common';
}

let state: PricePickerState | null = null;

export function createPricePicker(
  container: HTMLElement,
  options: PricePickerOptions
): PricePickerState | null {
  const {
    currentPrice,
    minPercent = -15,
    maxPercent = 15,
    onSelect
  } = options;
  
  // Validate price input
  if (!currentPrice || currentPrice <= 0 || !isFinite(currentPrice)) {
    container.innerHTML = `
      <div class="picker-error">
        <p class="picker-error-text">Price data unavailable</p>
        <p class="picker-error-hint">Please try again later</p>
      </div>
    `;
    return null;
  }
  
  const step = options.step || calculateStep(currentPrice);

  const roundedCurrent = roundToStep(currentPrice, step);
  const minPrice = roundToStep(currentPrice * (1 + minPercent / 100), step);
  const maxPrice = roundToStep(currentPrice * (1 + maxPercent / 100), step);

  const prices: number[] = [];
  for (let p = maxPrice; p >= minPrice; p -= step) {
    prices.push(p);
  }

  let currentIndex = prices.findIndex(p => p <= roundedCurrent);
  if (currentIndex === -1) currentIndex = Math.floor(prices.length / 2);

  const initialPercent = ((prices[currentIndex] - currentPrice) / currentPrice) * 100;
  state = {
    selectedPrice: prices[currentIndex],
    selectedPercent: initialPercent,
    selectedTier: getTier(initialPercent).toUpperCase(),
    prices,
    currentIndex
  };

  container.innerHTML = `
    <div class="price-picker">
      <div class="picker-header">
        <span class="picker-label">PREDICT CLOSING PRICE</span>
        <span class="picker-current">Current: ${formatPrice(currentPrice)}</span>
      </div>
      
      <div class="picker-list-container">
        <div class="picker-highlight"></div>
        <div class="picker-list" id="picker-list">
          ${prices.map((price, i) => {
            const percent = ((price - currentPrice) / currentPrice) * 100;
            const tier = getTier(percent);
            const isUp = price > roundedCurrent;
            const isDown = price < roundedCurrent;
            const isCurrent = price === roundedCurrent;
            return `
              <div class="picker-item ${isUp ? 'up' : ''} ${isDown ? 'down' : ''} ${isCurrent ? 'current' : ''}" 
                   data-index="${i}" data-price="${price}" data-percent="${percent.toFixed(2)}">
                <span class="item-price">${formatPrice(price)}</span>
                <span class="item-percent">${formatPercent(percent)}</span>
                <span class="item-tier ${tier}">${tier.toUpperCase()}</span>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    </div>
  `;

  const list = container.querySelector('#picker-list') as HTMLElement;
  const items = list.querySelectorAll('.picker-item');
  const itemHeight = 44;

  // Scroll to current price
  setTimeout(() => {
    const scrollTo = currentIndex * itemHeight - (list.clientHeight / 2) + (itemHeight / 2);
    list.scrollTop = scrollTo;
  }, 10);

  // Update selection on scroll
  function updateSelection() {
    const scrollCenter = list.scrollTop + list.clientHeight / 2;
    const newIndex = Math.round(scrollCenter / itemHeight - 0.5);
    const clampedIndex = Math.max(0, Math.min(prices.length - 1, newIndex));

    if (clampedIndex !== state!.currentIndex) {
      state!.currentIndex = clampedIndex;
      state!.selectedPrice = prices[clampedIndex];
      state!.selectedPercent = ((prices[clampedIndex] - currentPrice) / currentPrice) * 100;
      state!.selectedTier = getTier(state!.selectedPercent).toUpperCase();

      items.forEach((item, i) => {
        item.classList.toggle('selected', i === clampedIndex);
      });

      if (onSelect) {
        onSelect(state!.selectedPrice, state!.selectedPercent);
      }
    }
  }

  list.addEventListener('scroll', updateSelection);

  // Click to select
  items.forEach((item, i) => {
    item.addEventListener('click', () => {
      const scrollTo = i * itemHeight - (list.clientHeight / 2) + (itemHeight / 2);
      list.scrollTo({ top: scrollTo, behavior: 'smooth' });
    });
  });

  // Initial selection
  items[currentIndex]?.classList.add('selected');

  return state;
}

export function getPickerState(): PricePickerState | null {
  return state;
}
